/**
 * Repair single-backslash LaTeX inside model-emitted JSON before JSON.parse
 * (port of the backend's api/intelligence/exam_generation/jsonrepair.py).
 *
 * Gemini writes "$\frac{1}{2}$" with ONE backslash inside JSON strings. JSON.parse
 * then rejects it (\sqrt, \alpha, \( -> "Bad escaped character") or silently mangles
 * it (\times -> TAB + "imes", \frac -> form-feed + "rac", \neq -> newline + "eq").
 * Inside string literals only: keeps \" \\ \/ and \uXXXX; for \b \f \n \r \t reads
 * the letter run after the backslash and doubles the backslash only when that word
 * is a known LaTeX command (so "\nThe" stays a newline); doubles every other backslash.
 * Idempotent on already-correct JSON.
 */

const HEX4 = /^[0-9a-fA-F]{4}$/

const LATEX_ESCAPE_ALLOWLIST = new Set(`
bar beta begin bf big bigg Big Bigg bigl bigr binom bmod boldsymbol bot bullet
backslash boxed frac forall flat frown ne neq nu nabla not ni neg newline
nolimits nleq ngeq nsubseteq rho right rangle rceil rfloor rm rbrace rvert
times theta tau tan tanh to text textbf textit textrm tfrac tilde triangle
therefore top tt
rightarrow Rightarrow rightleftharpoons rightleftarrows rightharpoonup
rightharpoondown rbrack rparen rVert rgroup
notin nexists nless ngtr nmid nparallel nsubset nsupset nsupseteq ncong nsim
nearrow nwarrow natural
bigcup bigcap bigoplus bigotimes bigvee bigwedge bigsqcup bigodot biguplus
bigstar blacksquare blacktriangle bowtie boxplus boxtimes boxdot breve
texttt textdegree textsf textup textnormal textsc triangleq triangleleft
triangleright trianglelefteq trianglerighteq thicksim fbox
`.split(/\s+/).filter(Boolean))

const isAsciiLetter = (c: string) => /^[A-Za-z]$/.test(c)

export function repairLatexJson(text: string): string {
    const out: string[] = []
    const n = text.length
    let i = 0
    let inStr = false
    while (i < n) {
        const c = text[i]
        if (!inStr) {
            out.push(c)
            if (c === '"') inStr = true
            i += 1
            continue
        }
        if (c === '"') {
            inStr = false
            out.push(c)
            i += 1
            continue
        }
        if (c !== '\\') {
            out.push(c)
            i += 1
            continue
        }
        const nxt = i + 1 < n ? text[i + 1] : ''
        if (nxt === '"' || nxt === '\\' || nxt === '/') {
            out.push(c + nxt)
            i += 2
            continue
        }
        if (nxt === 'u' && HEX4.test(text.slice(i + 2, i + 6))) {
            out.push(text.slice(i, i + 6))
            i += 6
            continue
        }
        if (nxt && 'bfnrt'.includes(nxt)) {
            let j = i + 1
            while (j < n && isAsciiLetter(text[j])) j += 1
            const word = text.slice(i + 1, j)
            if (LATEX_ESCAPE_ALLOWLIST.has(word)) {
                out.push('\\\\' + word)
                i = j
            } else {
                out.push(c + nxt)
                i += 2
            }
            continue
        }
        out.push('\\\\')
        i += 1
    }
    return out.join('')
}
